/**
 * Подборки покупателям: что выдано, что открывали, что пора отозвать.
 *
 * Список живёт на сервере — подборка и есть серверная сущность, на устройстве
 * ей делать нечего. Без сети экран честно говорит об этом, а не притворяется
 * пустым.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../sync';
import { plural } from '@core';

interface Collection {
  id: string; title: string | null; stones: number;
  created_at: string; expires_at: string | null; revoked_at: string | null; opens: number;
}

export default function Collections({ onBack }: { onBack: () => void }) {
  const [list, setList] = useState<Collection[]>([]);
  const [problem, setProblem] = useState('');
  const [copied, setCopied] = useState('');

  async function load() {
    setProblem('');
    try {
      const res = await apiFetch('/api/collections');
      if (!res.ok) throw new Error(String(res.status));
      setList(((await res.json()) as { collections: Collection[] }).collections);
    } catch {
      setProblem('Список подборок живёт на сервере — без связи его не показать.');
    }
  }
  useEffect(() => { void load(); }, []);

  async function revoke(id: string) {
    await apiFetch(`/api/collections/${id}/revoke`, { method: 'POST' });
    await load();
  }

  async function copy(id: string) {
    try {
      await navigator.clipboard.writeText(`https://catalog.gemsnote.app/c/${id}`);
      setCopied(id);
    } catch { /* нет доступа к буферу */ }
  }

  const fmt = (d: string) => new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  const state = (c: Collection) =>
    c.revoked_at ? 'отозвана'
    : c.expires_at && new Date(c.expires_at) < new Date() ? 'истекла'
    : c.expires_at ? `до ${fmt(c.expires_at)}` : 'бессрочная';

  const alive = (c: Collection) => !c.revoked_at
    && (!c.expires_at || new Date(c.expires_at) > new Date());

  return (
    <div className="app tabbed">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Ещё</button>
        <h1 className="t">Подборки покупателям</h1>
        <p className="meta">Создаются в каталоге: Сравнить → отметить камни → «Покупателю»</p>
      </div>

      <div className="scroll">
        {problem && <div className="memo warn">{problem}</div>}

        {!problem && list.length === 0 && (
          <div className="blankslate">
            <b>Пока ни одной</b>
            Отметьте камни в каталоге и нажмите «Покупателю» — получится
            страница-витрина без цен и заметок.
          </div>
        )}

        {list.length > 0 && (
          <div className="list" style={{ marginTop: 4 }}>
            {list.map((c) => (
              <div className="row" key={c.id}>
                <span className="grow">
                  <span className="nm" style={alive(c) ? undefined : { color: 'var(--ink-3)' }}>
                    {c.title || `Подборка от ${fmt(c.created_at)}`}
                  </span>
                  <span className="sub">
                    {c.stones} {plural(c.stones, 'камень', 'камня', 'камней')} · {state(c)}
                    {c.opens > 0 && ` · открывали ${c.opens}`}
                  </span>
                </span>
                {alive(c) ? (
                  <>
                    <button className="btn-quiet" onClick={() => void copy(c.id)}>
                      {copied === c.id ? '✓' : 'Ссылка'}
                    </button>
                    <button className="btn-quiet" onClick={() => void revoke(c.id)}>Отозвать</button>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
